import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Plus, Minus, Trash2, Search, Printer, ShoppingBag, CreditCard, ChevronRight } from 'lucide-react';
import Loading from '../../components/Loading';

export default function POSBilling() {
    const toast = useToast();
    const [menuItems, setMenuItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    
    // POS Cart State
    const [cart, setCart] = useState([]);
    const [orderType, setOrderType] = useState('dine_in'); // 'dine_in' or 'takeaway'
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash', 'upi', 'card'
    const [restaurantInfo, setRestaurantInfo] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchData = async () => {
        try {
            // First fetch the dynamic restaurant context
            // We find restaurant details by searching the current tenant or admin profile context
            const userRes = await api.get('/api/restaurants/subscription/details');
            const resId = userRes.data.id;
            setRestaurantInfo(userRes.data);

            const [itemsRes, catsRes] = await Promise.all([
                api.get(`/api/menu/items?restaurantId=${resId}`),
                api.get(`/api/menu/categories?restaurantId=${resId}`)
            ]);

            setMenuItems(itemsRes.data);
            setCategories(catsRes.data);
        } catch (err) {
            console.error('Failed to load menu or configurations for POS', err);
            toast.error('Failed to load POS resources.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter Items
    const filteredItems = menuItems.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) || 
                              item.description.toLowerCase().includes(search.toLowerCase());
        
        if (selectedCategory === 'All') return matchesSearch;
        return matchesSearch && item.category_name === selectedCategory;
    });

    const addToCart = (item) => {
        if (item.stock_quantity <= 0) {
            toast.error('Item is out of stock!');
            return;
        }

        const existing = cart.find(cartItem => cartItem.id === item.id);
        if (existing) {
            if (existing.quantity >= item.stock_quantity) {
                toast.error(`Only ${item.stock_quantity} available in stock!`);
                return;
            }
            setCart(cart.map(cartItem => 
                cartItem.id === item.id 
                    ? { ...cartItem, quantity: cartItem.quantity + 1 }
                    : cartItem
            ));
        } else {
            setCart([...cart, { ...item, quantity: 1 }]);
        }
    };

    const updateQuantity = (itemId, delta) => {
        const item = cart.find(c => c.id === itemId);
        const originalItem = menuItems.find(m => m.id === itemId);
        
        if (delta > 0 && item.quantity >= originalItem.stock_quantity) {
            toast.error(`Only ${originalItem.stock_quantity} available in stock!`);
            return;
        }

        const newQty = item.quantity + delta;
        if (newQty <= 0) {
            removeFromCart(itemId);
        } else {
            setCart(cart.map(c => c.id === itemId ? { ...c, quantity: newQty } : c));
        }
    };

    const removeFromCart = (itemId) => {
        setCart(cart.filter(c => c.id !== itemId));
    };

    // Computations
    const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    const gstRate = 0.05; // 5% GST
    const gstAmount = Math.round(subtotal * gstRate * 100) / 100;
    const totalAmount = subtotal + gstAmount;

    // Direct Receipt Printing (Thermal printer optimized layout: 80mm roll width)
    const printThermalReceipt = (orderId, orderDetails) => {
        const printWindow = window.open('', '_blank');
        const itemsHtml = cart.map(item => `
            <tr>
                <td style="padding: 4px 0;">${item.name} x ${item.quantity}</td>
                <td style="padding: 4px 0; text-align: right;">₹${(parseFloat(item.price) * item.quantity).toFixed(2)}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
            <html>
            <head>
                <title>order_${orderId}</title>
                <style>
                    @page { margin: 0; }
                    body {
                        font-family: 'Courier New', Courier, monospace;
                        width: 76mm; /* Standard 80mm thermal width */
                        margin: 0;
                        padding: 10px;
                        font-size: 12px;
                        color: #000;
                        line-height: 1.4;
                    }
                    .text-center { text-align: center; }
                    .dashed-line { border-top: 1px dashed #000; margin: 8px 0; }
                    .bold { font-weight: bold; }
                    table { width: 100%; border-collapse: collapse; }
                    th, td { text-align: left; }
                </style>
            </head>
            <body>
                <div class="text-center">
                    <span style="font-size: 16px; font-weight: bold;">${restaurantInfo?.name || 'Aaharam Partner'}</span><br/>
                    <span>POS BILLING RECEIPT</span><br/>
                    <span>Anna Nagar, Chennai</span>
                </div>
                <div class="dashed-line"></div>
                <div>
                    <span><strong>Bill ID:</strong> POS-${orderId}</span><br/>
                    <span><strong>Date:</strong> ${new Date().toLocaleString()}</span><br/>
                    <span><strong>Type:</strong> ${orderType === 'dine_in' ? 'DINE-IN' : 'TAKEAWAY'}</span><br/>
                    ${customerName ? `<span><strong>Cust:</strong> ${customerName}</span><br/>` : ''}
                    ${customerPhone ? `<span><strong>Phone:</strong> ${customerPhone}</span><br/>` : ''}
                </div>
                <div class="dashed-line"></div>
                <table>
                    <thead>
                        <tr class="bold">
                            <th style="padding-bottom: 5px;">Item</th>
                            <th style="padding-bottom: 5px; text-align: right;">Amt</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsHtml}
                    </tbody>
                </table>
                <div class="dashed-line"></div>
                <table class="bold">
                    <tr>
                        <td>Subtotal</td>
                        <td style="text-align: right;">₹${subtotal.toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td>CGST/SGST (5%)</td>
                        <td style="text-align: right;">₹${gstAmount.toFixed(2)}</td>
                    </tr>
                    <tr style="font-size: 14px;">
                        <td>GRAND TOTAL</td>
                        <td style="text-align: right;">₹${totalAmount.toFixed(2)}</td>
                    </tr>
                </table>
                <div class="dashed-line"></div>
                <div class="text-center">
                    <span>Paid via: <span style="text-transform: uppercase;">${paymentMethod}</span></span><br/>
                    <span class="bold">THANK YOU! VISIT AGAIN</span><br/>
                    <span style="font-size: 8px;">Aaharam Cloud POS Terminal v1.0</span>
                </div>
                <script>
                    window.onload = function() {
                        document.title = "order_${orderId}";
                        window.print();
                        setTimeout(function() { window.close(); }, 500);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.title = `order_${orderId}`;
        printWindow.document.close();
    };

    const handleCheckout = async () => {
        if (cart.length === 0) {
            toast.error('Add items to create POS bill!');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                items: cart.map(c => ({ menuItemId: c.id, quantity: c.quantity })),
                orderType,
                customerName,
                customerPhone,
                paymentMethod,
                subtotalAmount: subtotal,
                taxAmount: gstAmount,
                finalTotalAmount: totalAmount
            };

            const response = await api.post('/api/orders/pos', payload);
            toast.success(`POS Order #${response.data.id} placed & printed!`);
            
            // Print receipt dynamically
            printThermalReceipt(response.data.id, response.data);

            // Reset cart states
            setCart([]);
            setCustomerName('');
            setCustomerPhone('');
            fetchData(); // reload stock counts
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to place POS purchase');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loading />;

    return (
        <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto text-left h-[calc(100vh-8rem)]">
            {/* Left Section - Items Grid */}
            <div className="flex-1 flex flex-col space-y-4 h-full overflow-hidden">
                <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900">Direct POS Billing</h1>
                        <p className="text-xs text-gray-400 mt-0.5">Quick order placement and receipt printing for walk-in/dine-in customers.</p>
                    </div>
                    {/* Search */}
                    <div className="relative w-full sm:w-64">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                        <input
                            type="text"
                            placeholder="Search menu..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input-field pl-9 py-2 text-sm w-full"
                        />
                    </div>
                </div>

                {/* Categories Tab Selector */}
                <div className="flex space-x-2 overflow-x-auto pb-1.5 scrollbar-thin">
                    <button
                        onClick={() => setSelectedCategory('All')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all whitespace-nowrap ${
                            selectedCategory === 'All'
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        All Items
                    </button>
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.name)}
                            className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all whitespace-nowrap ${
                                selectedCategory === cat.name
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>

                {/* Menu Grid */}
                <div className="flex-1 overflow-y-auto pr-1">
                    {filteredItems.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {filteredItems.map((item) => (
                                <div 
                                    key={item.id}
                                    onClick={() => addToCart(item)}
                                    className={`bg-white p-3 rounded-2xl border-2 border-gray-100 hover:border-primary-500 cursor-pointer transition-all flex flex-col justify-between shadow-sm relative ${
                                        item.stock_quantity <= 0 ? 'opacity-65 pointer-events-none' : ''
                                    }`}
                                >
                                    {/* Thumbnail Image */}
                                    <div className="h-28 w-full rounded-xl overflow-hidden bg-gray-50 mb-3">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold text-xs">
                                                No Image
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-bold text-gray-800 line-clamp-1">{item.name}</h3>
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-xs font-black text-primary-600">₹{item.price}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                                item.stock_quantity <= 5 
                                                    ? 'bg-orange-100 text-orange-700' 
                                                    : 'bg-slate-100 text-slate-500'
                                            }`}>
                                                Stock: {item.stock_quantity}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-white border border-gray-200 rounded-3xl text-gray-400 font-medium">
                            No menu items matched your search query.
                        </div>
                    )}
                </div>
            </div>

            {/* Right Section - Current Bill Panel */}
            <div className="w-full lg:w-96 bg-white border border-gray-200 rounded-3xl shadow-md p-5 flex flex-col justify-between h-full overflow-hidden">
                <div className="space-y-4 flex-1 flex flex-col overflow-hidden">
                    <h2 className="text-md font-bold text-gray-900 flex items-center border-b pb-2">
                        <ShoppingBag className="w-4 h-4 text-primary-500 mr-2" />
                        Current Bill Cart
                    </h2>

                    {/* Customer Info Form */}
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="text"
                            placeholder="Cust. Name (Opt)"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            className="input-field text-xs py-1.5"
                        />
                        <input
                            type="text"
                            placeholder="Cust. Phone (Opt)"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            className="input-field text-xs py-1.5"
                        />
                    </div>

                    {/* Order Type Selector */}
                    <div className="grid grid-cols-2 bg-gray-100 p-1 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setOrderType('dine_in')}
                            className={`py-1.5 text-[10px] font-bold rounded-lg transition-all ${
                                orderType === 'dine_in' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
                            }`}
                        >
                            Dine-In Table
                        </button>
                        <button
                            type="button"
                            onClick={() => setOrderType('takeaway')}
                            className={`py-1.5 text-[10px] font-bold rounded-lg transition-all ${
                                orderType === 'takeaway' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500'
                            }`}
                        >
                            Takeaway Store
                        </button>
                    </div>

                    {/* Cart Items List */}
                    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                        {cart.length > 0 ? (
                            cart.map((item) => (
                                <div key={item.id} className="flex justify-between items-center p-2.5 bg-slate-50 border border-slate-100 rounded-xl">
                                    <div className="text-left flex-1 min-w-0 pr-3">
                                        <p className="text-xs font-bold text-gray-800 truncate">{item.name}</p>
                                        <p className="text-[10px] text-primary-500 font-bold mt-0.5">₹{item.price}</p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => updateQuantity(item.id, -1)}
                                            className="p-1 rounded-full bg-white hover:bg-gray-100 border text-gray-500"
                                        >
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQuantity(item.id, 1)}
                                            className="p-1 rounded-full bg-white hover:bg-gray-100 border text-gray-500"
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                        <button
                                            onClick={() => removeFromCart(item.id)}
                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg ml-1"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-20 text-gray-400 text-xs font-medium">
                                Cart is empty. Select menu cards on the left.
                            </div>
                        )}
                    </div>
                </div>

                {/* Subtotal, Tax & Checkout */}
                <div className="border-t pt-4 space-y-4 bg-white">
                    <div className="space-y-1.5 text-xs text-left">
                        <div className="flex justify-between text-gray-500">
                            <span>Subtotal</span>
                            <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                            <span>GST (5%)</span>
                            <span className="font-semibold">₹{gstAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-900 font-black text-sm border-t pt-2 mt-1">
                            <span>Grand Total</span>
                            <span>₹{totalAmount.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Payment Mode */}
                    <div className="space-y-1.5">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest text-left">Payment Mode</p>
                        <div className="grid grid-cols-3 gap-2">
                            {['cash', 'upi', 'card'].map((method) => (
                                <button
                                    key={method}
                                    type="button"
                                    onClick={() => setPaymentMethod(method)}
                                    className={`py-2 text-[10px] font-bold uppercase rounded-lg border-2 transition-all ${
                                        paymentMethod === method
                                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                                            : 'border-gray-100 hover:border-gray-200 text-gray-500'
                                    }`}
                                >
                                    {method}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Submit & print button */}
                    <button
                        onClick={handleCheckout}
                        disabled={submitting || cart.length === 0}
                        className="btn-primary w-full py-3.5 font-bold uppercase tracking-widest text-xs flex items-center justify-center space-x-2"
                    >
                        <Printer className="w-4 h-4" />
                        <span>{submitting ? 'Creating Order...' : 'Generate & Print Bill'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
